<template>
  <layout-default>
    <div class="advisors-dashboard">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <summary-card
        :filter="defaultQueryParams.range"
        :filter-options="availableQueryParams.range"
        :sort="currentSorting"
        :sorting-options="sortingOptions"
        @change-filter="changeFilter"
        @change-sort="changeSort"
        :participants="summaryData.advisors"
        :positiveActions="summaryData.positiveActions"
        :avgDailyRating="summaryData.avgDailyRating"
        :totalDailyRating="summaryData.totalDailyRating"
        :journalEntries="summaryData.journalEntries"
        :swapsAddedTo="summaryData.swapsAddedTo"
        :swapsCompleted="summaryData.swapsCompleted"
        :swapsInProgress="summaryData.swapsInProgress"
      />
      <div class="d-flex justify-end px-2">
        <v-checkbox
          label="Include all advisors"
          @change="value => changeShowAll(value)"
          v-model="defaultQueryParams.showAll"
        />
      </div>
      <div class="advisors-list mt-6">
        <template v-if="isDashboardContent">
          <advisor-list-item
            v-for="advisor in advisors"
            :key="
              `advisor-${advisor.firstname}${advisor.lastname}-${advisor.profileId}`
            "
            v-bind="advisor"
            @navigate-to-advisor="navigateToAdvisor"
            :unreadChatMessages="unreadChatMessages(advisor.chatId)"
          />
        </template>
      </div>
    </div>
  </layout-default>
</template>

<script>
import LayoutDefault from '@/layouts/LayoutDefault.vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { debounce } from '@/helpers/debounce'
import SummaryCard from '@/components/participantsDashboard/SummaryCard.vue'
import TenantTextsMixin, {
  tenantTexts as normalTenentTexts,
} from '@/mixins/tenantTexts'
import RouterMixin from '@/mixins/router'
import FeaturesSettingsMixin from '@/mixins/featuresSettings'
import AdvisorListItem from '@/components/advisorsDashboard/AdvisorListItem.vue'

export default {
  name: 'AdvisorsDashboard',
  components: {
    AdvisorListItem,
    SummaryCard,
    LayoutDefault,
  },
  mixins: [TenantTextsMixin, RouterMixin, FeaturesSettingsMixin],
  data() {
    return {
      defaultQueryParams: {
        range: 'week',
        showAll: false,
      },
      availableQueryParams: {
        range: [
          { text: 'Today', value: 'today' },
          { text: 'Week', value: 'week' },
          { text: 'Month', value: 'month' },
          { text: 'Year', value: 'year' },
        ],
      },
      currentSorting: 'lastActivitySort',
      sortingOptions: [
        { text: 'Advisors Alphabetized', value: 'fullnameSort' },
        {
          text: normalTenentTexts.advisorsDashboard.routinesSkipped,
          value: 'routinesSkippedSort',
        },
        { text: 'The most recent engagement', value: 'lastActivitySort' },
      ],
    }
  },
  computed: {
    ...mapGetters('userStates', ['getRolename', 'getProfileId']),
    ...mapState('chatStates', ['unreadMessagesObject']),
    ...mapState('practicesStates', [
      'practiceUserAdvisorsDashboard',
      'loading',
    ]),
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    isDashboardContent() {
      return !!this.practiceUserAdvisorsDashboard?.content?.length
    },
    isCompanyAdmin() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Administrator
      )
    },
    summaryData() {
      let summaryData = {
        advisors: 0,
        positiveActions: 0,
        avgDailyRating: 0,
        totalDailyRating: 0,
        journalEntries: 0,
        swapsAddedTo: 0,
        swapsCompleted: 0,
        swapsInProgress: 0,
      }

      // Return zeros when no data
      if (!this.isDashboardContent) return summaryData

      // Sum all required values
      summaryData = this.practiceUserAdvisorsDashboard.content.reduce(
        (acc, curVal) => {
          return {
            advisors: acc.advisors + 1,
            positiveActions:
              acc.positiveActions + (curVal?.positiveActions || 0),
            avgDailyRating: acc.avgDailyRating + (curVal?.ratingDaySum || 0),
            totalDailyRating:
              acc.totalDailyRating + (curVal?.ratingDayCount || 0),
            journalEntries: acc.journalEntries + (curVal?.journalEntries || 0),
            swapsAddedTo: acc.swapsAddedTo + (curVal?.swapActivitiesTotal || 0),
            swapsCompleted: acc.swapsCompleted + (curVal?.swapCompleted || 0),
            swapsInProgress:
              acc.swapsInProgress + (curVal?.swapInProgress || 0),
          }
        },
        summaryData,
      )

      if (summaryData.totalDailyRating > 0) {
        summaryData.avgDailyRating =
          summaryData.avgDailyRating / summaryData.totalDailyRating
      }

      return summaryData
    },
    advisors() {
      if (!this.isDashboardContent) return []

      const allAdvisors = [...this.practiceUserAdvisorsDashboard.content]

      const sortedAdvisors = allAdvisors.sort((a, b) => {
        let Afullname = ''
        let Bfullname = ''

        let AlastActivity = this.$moment(a?.lastActivity || '2000-01-01')
        let BlastActivity = this.$moment(b?.lastActivity || '2000-01-01')
        let DiffLastActivity = AlastActivity.diff(BlastActivity)

        switch (this.currentSorting) {
          case 'fullnameSort':
            Afullname = a.firstname
            if (Afullname.length > 0) Afullname += ' '
            Afullname += a.lastname
            Bfullname = b.firstname
            if (Bfullname.length > 0) Bfullname += ' '
            Bfullname += b.lastname

            if (Afullname < Bfullname) return -1
            if (Afullname > Bfullname) return 1
            return 0
          case 'routinesSkippedSort':
            if (a.routinesSkipped < b.routinesSkipped) return 1
            if (a.routinesSkipped > b.routinesSkipped) return -1
            return 0
          case 'lastActivitySort':
            if (DiffLastActivity > 0) return -1
            if (DiffLastActivity < 0) return 1
            return 0
        }
      })

      return sortedAdvisors
    },
  },
  mounted() {
    // redirect if cannot go to the dashboard
    if (this.dictionaryPracticeUserRole[this.getRolename] !== 4) {
      this.$router.push('/participants')
      return
    }

    this.getData()
  },
  methods: {
    ...mapActions('practicesStates', ['getPracticeUserAdvisorsDashboard']),
    ...mapMutations('patientsStates', ['setCurrentAdvisorDashboardName']),
    getData() {
      // redirect if cannot go to the dashboard
      if (this.dictionaryPracticeUserRole[this.getRolename] !== 4) {
        this.$router.push('/participants')
        return
      }

      debounce(
        () => {
          this.getPracticeUserAdvisorsDashboard(this.defaultQueryParams)
        },
        100,
        'getPracticeUserDashboard',
      )
    },
    unreadChatMessages(chatId = '') {
      return this.unreadMessagesObject?.[chatId] || 0
    },
    changeSort(sort = '') {
      let selectedSort =
        this.sortingOptions.find(el => el.value === sort)?.value || ''

      if (!selectedSort) selectedSort = 'lastActivitySort'

      this.currentSorting = selectedSort
    },
    changeFilter(filter = '') {
      let selectedFilter =
        this.availableQueryParams.range.find(el => el.value === filter)
          ?.value || ''

      if (!selectedFilter) selectedFilter = 'week'

      this.defaultQueryParams.range = selectedFilter

      this.getPracticeUserAdvisorsDashboard(this.defaultQueryParams)
    },
    changeShowAll(value = false) {
      const showAll = value === true || value === 'true'

      this.defaultQueryParams.showAll = showAll

      this.getPracticeUserAdvisorsDashboard(this.defaultQueryParams)
    },
    navigateToAdvisor(id, type, advisorFullName) {
      if (!id) return

      let route = false
      if (advisorFullName) {
        this.setCurrentAdvisorDashboardName(advisorFullName)
      }

      switch (type) {
        case 'chat':
          route = {
            name: 'Chat',
            path: '/chat',
            params: {
              chatId: id,
            },
          }
          break
        default:
          route = {
            name: 'Advisors Dashboard Details',
            path: '/advisors-dashboard/:id',
            params: { id, isCompanyAdmin: this.isCompanyAdmin },
          }
      }
      this.$router.push(route)
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.advisors-dashboard {
  padding: 1.5 * $gutter $gutter 0 $gutter;

  .advisors-list {
    padding-left: 0.5 * $gutter;
    padding-right: 0.5 * $gutter;
  }
}
</style>
